/**
 * Hook for global search state and data fetching.
 * Combines client-side navigation search (modules + pages) with debounced
 * server-side entity search.
 */
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from './useDebounce';
import { searchApi, SearchFilters, SearchResponse } from '../services/searchApi';
import {
  getSuggestedPages,
  searchNavigation,
  NavSearchResult,
} from '../components/global-search/searchConfig';
import { useModule } from '../contexts/ModuleContext';
import { useNavItemAccess } from './useNavItemAccess';

export const searchKeys = {
  all: ['global-search'] as const,
  query: (q: string, filters: SearchFilters) =>
    [...searchKeys.all, q, filters] as const,
};

interface UseGlobalSearchOptions {
  defaultModule?: string;
}

interface UseGlobalSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  filters: SearchFilters;
  setFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  moduleResults: NavSearchResult[];
  pageResults: NavSearchResult[];
  suggestedPages: NavSearchResult[];
  entityQuery: ReturnType<typeof useQuery<SearchResponse>>;
  /** Headline count of everything the results list shows. */
  resultCount: number;
  isLoading: boolean;
}

export function useGlobalSearch(options?: UseGlobalSearchOptions): UseGlobalSearchReturn {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    modules: options?.defaultModule ? [options.defaultModule] : ['all'],
    entityTypes: [],
    dateRange: null,
  });

  const debouncedQuery = useDebounce(query, 300);
  // Nav-item-level access (admin/sysadmin, connected accounts, forwarder
  // persona) — the same gate the sidebar applies, so search never lists a page
  // the sidebar hides. Also carries the persona flag used for module naming.
  const access = useNavItemAccess();

  // Navigation results are drawn from exactly what the module picker offers —
  // the modules this user can actually open. Disabled, above-ceiling,
  // role-gated and hidden modules never surface a module or page result.
  const { pickerModules, pinnedModules } = useModule();
  const navigableModules = useMemo(
    () => [...pickerModules, ...pinnedModules],
    [pickerModules, pinnedModules],
  );

  // Client-side module + page search (instant)
  const { modules: moduleResults, pages: pageResults } = useMemo(
    () => searchNavigation(navigableModules, query, filters.modules, access),
    [navigableModules, query, filters.modules, access],
  );

  // Empty-query state: a handful of pages to get started with.
  const suggestedPages = useMemo(
    () => getSuggestedPages(navigableModules, filters.modules, access),
    [navigableModules, filters.modules, access],
  );

  // Server-side entity search (debounced)
  const entityQuery = useQuery({
    queryKey: searchKeys.query(debouncedQuery, filters),
    queryFn: () => searchApi.search(debouncedQuery, filters),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
    placeholderData: (prev: SearchResponse | undefined) => prev,
  });

  const isLoading =
    debouncedQuery.length >= 2 &&
    (entityQuery.isLoading || entityQuery.isFetching);

  // Everything the results list shows. `total_count` covers entities + tags
  // only — `searchApi` asks the server not to run its own page pass, since this
  // UI renders the richer, access-scoped navigation matches below instead.
  // The nav counts track the raw query while the entity count trails the 300ms
  // debounce, so a fast typist can briefly see the two halves out of step.
  const resultCount =
    (entityQuery.data?.total_count ?? 0) + moduleResults.length + pageResults.length;

  return {
    query,
    setQuery,
    filters,
    setFilters,
    moduleResults,
    pageResults,
    suggestedPages,
    entityQuery,
    resultCount,
    isLoading,
  };
}
